// ─── 🎓 Le ore riservate di un maestro ───────────────────────────────────────
//
// 🗣️ 18/10/2026 — mockup «Ore riservate», approvato. Un posto solo, in
// Impostazioni › Maestri: la Segreteria riserva a un maestro un **campo preciso**
// in certe ore, ogni settimana oppure un giorno solo. In quelle ore, su quel
// campo:
//   · le partite dal bot non si offrono e non si scrivono (`ORA_RISERVATA`);
//   · la Segreteria dal calendario prenota lo stesso, dopo una domanda;
//   · le lezioni di quel maestro sì: sono le sue ore, per lui e per il bot.
//
// ⭐ È l'unico posto in cui si decide **cosa è riservato**: lo leggono la
// disponibilità del ponte, i controlli delle scritture, il perimetro del maestro
// (`perimetro-maestro.ts`) e il calendario della Segreteria. Lo schema dei dati
// (`oraRiservataSchema`) sta in `circolo.ts`, accanto a quello delle righe.
//
// 🧩 Le righe sono quelle di `instructor_slots` (⇒ `BandaMaestro`). Una riga
// senza campo (le «Tutti i campi» di prima) è **da completare**: qui non esiste.
//
// 🔒 Modulo puro: niente database, niente orologio.
import { giornoDellaData, GIORNI, ORDINE_SETTIMANA, } from './circolo.js';
import { minutiDaOra, vuoleMaestro } from './calendario.js';
/** Una riga vale quel giorno: lo stesso giorno della settimana, oppure proprio quella data. */
export function valeQuelGiorno(r, date) {
    if (r.onDate !== null)
        return r.onDate === date;
    return r.weekday !== null && r.weekday === giornoDellaData(date);
}
/** ⚖️ Una riga senza campo non riserva niente: è da completare. */
export function completa(b) {
    return b.active && b.courtId !== null;
}
/** Le ore riservate di quel giorno su quel campo, in ordine di ora. */
export function riservateDelGiorno(ore, date, courtId) {
    return ore
        .filter((o) => o.courtId === courtId && valeQuelGiorno(o, date))
        .sort((a, b) => a.startsAtTime.localeCompare(b.startsAtTime));
}
function toccaNelGiorno(ore, courtId, date, da, a) {
    return (ore.find((o) => o.courtId === courtId &&
        valeQuelGiorno(o, date) &&
        minutiDaOra(o.startsAtTime) < a &&
        minutiDaOra(o.endsAtTime) > da) ?? null);
}
/**
 * ⭐⭐ **Dal `daMinuto` all'`aMinuto` di `date`, su quel campo, si tocca un'ora
 * riservata?** Gli estremi che si toccano non contano (una partita che finisce
 * alle 9:00 non tocca le ore dalle 9:00). 🌙 Quello che passa la mezzanotte si
 * guarda anche nel giorno dopo.
 */
export function riservataCheTocca(ore, courtId, date, daMinuto, aMinuto) {
    const oggi = toccaNelGiorno(ore, courtId, date, daMinuto, Math.min(aMinuto, 1440));
    if (oggi || aMinuto <= 1440)
        return oggi;
    const dopo = new Date(Date.parse(`${date}T00:00:00Z`) + 86_400_000).toISOString().slice(0, 10);
    return toccaNelGiorno(ore, courtId, dopo, 0, aMinuto - 1440);
}
/**
 * ⭐ **Questa prenotazione, lì, entra nelle ore riservate di qualcuno?** Una
 * lezione (o un allenamento, un corso: i tipi col maestro) di quello stesso
 * maestro no — sono le sue ore. Tutto il resto sì.
 */
export function riservataPerLaPrenotazione(ore, c, tipo, instructorId) {
    const da = minutiDaOra(c.time);
    const suoi = vuoleMaestro(tipo) && instructorId !== null;
    const altre = suoi ? ore.filter((o) => o.instructorId !== instructorId) : ore;
    return riservataCheTocca(altre, c.courtId, c.date, da, da + c.durationMinutes);
}
/** 🎓 Il «no» del ponte a una partita lì, detto al socio con la strada. */
export const ORA_RISERVATA_AL_SOCIO = 'Quell’ora su quel campo è riservata alle lezioni: scegli pure un altro orario.';
/** 🎓 La domanda alla Segreteria, prima di prenotare comunque in un'ora riservata. */
export function domandaOraRiservata(r, tipo) {
    const cosa = {
        partita: ' una partita',
        torneo: ' il torneo',
        manutenzione: ' la manutenzione',
    };
    return `Questo slot è riservato a ${r.instructorName} per le lezioni. Prenoti comunque${cosa[tipo] ?? ''}?`;
}
/** «Sab», «Lun»: le tre lettere del giorno. */
const corto = (g) => GIORNI[g].slice(0, 3);
/** «9:00»: l'ora senza lo zero davanti, come si legge. */
const ora = (t) => t.replace(/^0(\d)/, '$1');
/** «18/10»: un giorno solo, nel riassunto. */
const giornoCorto = (d) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;
/**
 * 🎓 Il riassunto della colonna «Ore riservate» in Impostazioni › Maestri:
 * «Sab Campo 2 9:00–13:00 · Mar Campo 1 18:00–20:00 · 25/10 Campo 3 10:00–12:00»,
 * oppure «Ancora da aggiungere». Le righe senza campo si dicono «da completare».
 * Prima quelle di ogni settimana (da lunedì), poi i giorni singoli ancora da venire.
 */
export function riassuntoOreRiservate(bande, nomeCampo, oggi) {
    const vive = bande.filter((b) => b.active && (b.onDate === null || b.onDate >= oggi));
    if (vive.length === 0)
        return 'Ancora da aggiungere';
    const posto = (g) => (g === null ? 99 : ORDINE_SETTIMANA.indexOf(g));
    const ordinate = [...vive].sort((a, b) => Number(a.onDate !== null) - Number(b.onDate !== null) ||
        posto(a.weekday) - posto(b.weekday) ||
        (a.onDate ?? '').localeCompare(b.onDate ?? '') ||
        a.startsAtTime.localeCompare(b.startsAtTime));
    return ordinate
        .map((b) => {
        const quando = b.onDate !== null ? giornoCorto(b.onDate) : corto(b.weekday);
        const dove = b.courtId === null ? 'campo da scegliere' : nomeCampo(b.courtId);
        return `${quando} ${dove} ${ora(b.startsAtTime)}–${ora(b.endsAtTime)}`;
    })
        .join(' · ');
}
//# sourceMappingURL=ore-riservate.js.map